import { StyleSheet } from '@react-pdf/renderer';
import { REPORT_FONT_FAMILY } from '../sdlcReactPdf/fonts';

// Palette and sizing translated from the original HTML report's REPORT_CSS
// (utils/pdf/htmlAnalysisReport/styles.ts) into react-pdf primitives. Only
// font weights 400/600/700 are registered, so the CSS 800/900 weights map to
// 700 here.
export const colors = {
  bgPrimary: '#f8fafc',
  card: '#ffffff',
  border: '#e2e8f0',
  borderGlow: '#bfe8e1',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  teal: '#14b8a6',
  green: '#22c55e',
  greenInk: '#16a34a',
  amber: '#f59e0b',
  amberInk: '#d97706',
  red: '#ef4444',
  blue: '#3b82f6',
  purple: '#8b5cf6',
};

export function scoreColor(score: number): string {
  if (score >= 70) return colors.green;
  if (score >= 40) return colors.amber;
  return colors.red;
}

export function severityColors(severity?: string): { backgroundColor: string; color: string } {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return { backgroundColor: '#fdecec', color: colors.red };
    case 'high':
      return { backgroundColor: '#fdeee0', color: '#ea580c' };
    case 'medium':
      return { backgroundColor: '#fef3e0', color: colors.amberInk };
    default:
      return { backgroundColor: '#eaf1fd', color: colors.blue };
  }
}

export function recTypeColors(kind: 'add' | 'replace' | 'fix'): {
  backgroundColor: string;
  color: string;
} {
  switch (kind) {
    case 'replace':
      return { backgroundColor: '#f3eefe', color: colors.purple };
    case 'fix':
      return { backgroundColor: '#fef3e0', color: colors.amberInk };
    default:
      return { backgroundColor: '#e6faf7', color: colors.teal };
  }
}

export const styles = StyleSheet.create({
  page: {
    fontFamily: REPORT_FONT_FAMILY,
    fontSize: 8.5,
    lineHeight: 1.5,
    color: colors.textPrimary,
    backgroundColor: colors.card,
    paddingVertical: 34,
    paddingHorizontal: 38,
  },

  glass: {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 12,
  },

  sectionTitle: { fontSize: 15, fontWeight: 700, color: colors.textPrimary, marginBottom: 8 },
  divider: { borderTopWidth: 1, borderColor: colors.border, marginVertical: 12 },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.borderGlow,
    borderRadius: 14,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', flex: 1, paddingRight: 12 },
  headerTextBlock: { flex: 1 },
  logo: { width: 40, height: 40, borderRadius: 10, marginRight: 12, objectFit: 'contain' },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 10,
    marginRight: 12,
    backgroundColor: colors.teal,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoFallbackText: { fontSize: 20, fontWeight: 700, color: '#ffffff' },
  footerLogoFallback: {
    width: 18,
    height: 18,
    borderRadius: 5,
    marginRight: 5,
    backgroundColor: colors.teal,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerLogoFallbackText: { fontSize: 10, fontWeight: 700, color: '#ffffff' },
  headerTitle: { fontSize: 10, fontWeight: 600, color: colors.textSecondary },
  headerRepo: { fontSize: 17, fontWeight: 700, color: colors.textPrimary, marginTop: 2 },
  headerMeta: { fontSize: 7.5, color: colors.textMuted, marginTop: 7 },
  headerRight: { alignItems: 'flex-end', flexShrink: 0, width: 88 },
  healthLabel: {
    fontSize: 7,
    color: colors.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  healthScore: { fontSize: 34, fontWeight: 700, lineHeight: 1, marginTop: 2 },
  healthDenom: { fontSize: 12, fontWeight: 400, color: colors.textMuted },

  // Executive summary metric cards
  metricsRow: { flexDirection: 'row', marginBottom: 12 },
  metricCell: { width: '25%', paddingRight: 8 },
  metricCard: {
    backgroundColor: '#f0fbfa',
    borderWidth: 1,
    borderColor: '#d3f1ec',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
  },
  metricValue: { fontSize: 18, fontWeight: 700, color: colors.textPrimary, lineHeight: 1 },
  metricLabel: {
    width: '100%',
    fontSize: 7,
    color: colors.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginTop: 5,
    textAlign: 'center',
  },
  metricSub: {
    width: '100%',
    fontSize: 7,
    color: colors.textSecondary,
    marginTop: 4,
    textAlign: 'center',
  },

  summaryCard: { padding: 14, marginBottom: 4 },
  summaryText: { fontSize: 9, color: colors.textSecondary, lineHeight: 1.6 },
  bold: { fontWeight: 700, color: colors.textPrimary },

  // Pipeline
  pipelineCard: { padding: 14, marginBottom: 12 },
  pipelineTrack: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'center' },
  pipelineCol: { alignItems: 'center', width: 64 },
  node: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
  },
  nodeCovered: { backgroundColor: '#eafaf0', borderColor: '#8fdcab' },
  nodeGap: { backgroundColor: '#fdf0f0', borderColor: '#f2b8b8', borderStyle: 'dashed' },
  stageName: { fontSize: 8.5, fontWeight: 600, color: colors.textSecondary, marginTop: 6 },
  stageNameGap: { color: colors.red },
  connector: { flex: 1, marginTop: 19 },
  connectorLine: { height: 2.5, borderRadius: 2 },

  // Category bars
  catTable: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 },
  catCell: { width: '50%' },
  catCellPadRight: { paddingRight: 6 },
  catRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 9,
    marginBottom: 6,
  },
  catName: { fontSize: 8.5, fontWeight: 600, color: colors.textSecondary, flex: 1 },
  catBarBg: {
    width: 56,
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    marginHorizontal: 6,
  },
  catBarFill: { height: 6, borderRadius: 3 },
  catScore: { fontSize: 8.5, fontWeight: 700, width: 20, textAlign: 'right' },
  catBreakdown: { width: '100%', fontSize: 6.5, color: colors.textMuted, marginTop: 3 },

  // Two-column findings
  twoCol: { flexDirection: 'row', marginBottom: 8 },
  colHalf: { width: '50%' },
  colTitle: { fontSize: 11, fontWeight: 700, color: colors.textPrimary, marginBottom: 8 },

  sevBadge: {
    paddingVertical: 1.5,
    paddingHorizontal: 5,
    borderRadius: 4,
    fontSize: 6.5,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  typeBadge: {
    paddingVertical: 1.5,
    paddingHorizontal: 4,
    borderRadius: 4,
    fontSize: 6.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    marginLeft: 5,
  },

  gapItem: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 10,
    marginBottom: 6,
  },
  gapHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 3 },
  gapTitle: { fontSize: 9, fontWeight: 600, color: colors.textPrimary, marginLeft: 5, flex: 1 },
  gapDesc: { fontSize: 8, color: colors.textSecondary, lineHeight: 1.5 },

  strengthItem: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 9,
    marginBottom: 5,
  },
  strengthIcon: { color: colors.green, fontSize: 9, marginRight: 6 },
  strengthTitle: { fontSize: 8.5, fontWeight: 600, color: colors.textPrimary },
  strengthDesc: { fontSize: 7.5, color: colors.textSecondary, marginTop: 2 },

  // Recommendations grid
  recGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  recCell: { width: '50%' },
  recItem: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 12,
    padding: 11,
    marginBottom: 8,
  },
  recNumber: { fontSize: 15, fontWeight: 700, color: colors.textMuted, width: 18 },
  recContent: { flex: 1 },
  recHeader: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 },
  recTitle: {
    fontSize: 9,
    fontWeight: 600,
    color: colors.textPrimary,
    width: '100%',
    marginTop: 3,
  },
  recReason: { fontSize: 8, color: colors.textSecondary, lineHeight: 1.5, marginBottom: 5 },
  recMeta: { fontSize: 7, color: colors.textMuted, marginBottom: 5 },
  lfhrBadge: {
    paddingVertical: 1.5,
    paddingHorizontal: 6,
    borderRadius: 9999,
    fontSize: 6.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    backgroundColor: '#e6faf7',
    color: colors.teal,
    marginLeft: 5,
  },
  stepRow: { flexDirection: 'row', marginBottom: 2 },
  stepDot: { width: 10, fontSize: 8, color: colors.teal },
  stepText: { flex: 1, fontSize: 7.5, color: colors.textSecondary, lineHeight: 1.4 },

  // Footer
  footer: {
    borderTopWidth: 1,
    borderColor: colors.border,
    paddingTop: 12,
    marginTop: 12,
    alignItems: 'center',
  },
  footerBrand: { flexDirection: 'row', alignItems: 'center', marginBottom: 5 },
  footerLogo: { width: 18, height: 18, borderRadius: 5, marginRight: 5, objectFit: 'contain' },
  footerBrandText: { fontSize: 7.5, fontWeight: 600, color: colors.textSecondary },
  footerText: { fontSize: 7.5, color: colors.textMuted },
  footerDisclaimer: {
    fontSize: 6.5,
    color: colors.textMuted,
    marginTop: 4,
    textAlign: 'center',
    maxWidth: 380,
    lineHeight: 1.4,
  },

  // Appendix
  appendixTitle: { fontSize: 13, fontWeight: 700, color: colors.textPrimary, marginBottom: 4 },
  appendixNote: { fontSize: 8, color: colors.textMuted, marginBottom: 14 },
  toolsStageGroup: { marginBottom: 14 },
  toolsStageTitle: {
    fontSize: 7.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: colors.textMuted,
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderColor: colors.border,
  },
  toolsEmpty: { fontSize: 8, color: colors.textMuted, paddingVertical: 4 },
  toolsGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  toolCell: { width: '25%', paddingRight: 6, marginBottom: 6 },
  toolCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 8,
    alignItems: 'center',
  },
  toolName: { fontSize: 8, fontWeight: 600, color: colors.textPrimary, textAlign: 'center' },
  toolType: { fontSize: 6.5, color: colors.textMuted, marginTop: 1, textAlign: 'center' },
  tierBadge: {
    marginTop: 4,
    paddingVertical: 1.5,
    paddingHorizontal: 4,
    borderRadius: 4,
    fontSize: 6,
    fontWeight: 700,
    textTransform: 'uppercase',
    backgroundColor: '#eafaf0',
    color: colors.greenInk,
  },
});

// Solid tint for the connector; the CSS dashed/gradient variants collapse to a
// single accent in react-pdf.
export function connectorColor(kind: 'ok' | 'gap' | 'transition' | 'transition-rev'): string {
  return kind === 'gap' ? '#f2b8b8' : kind === 'ok' ? '#8fdcab' : '#d8c9a0';
}
